import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Popover, Button, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      const time = dayjs().hour(hour).minute(minute).second(0);
      options.push(time.format('h:mm A'));
    }
  }
  return options;
};

function getNextAvailableSlot() {
  const now = new Date();

  // Round up to next 15-min mark
  const minutes = now.getMinutes();
  const remainder = 15 - (minutes % 15);
  now.setMinutes(minutes + remainder, 0, 0);

  const startTime = now.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  });
  
  const endTimeDate = new Date(now.getTime() + 30 * 60000); // +30 mins
  const endTime = endTimeDate.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  });

  return { startTime, endTime };
}


const timeOptions = generateTimeOptions();

/** MUI `small` outlined TextField height — match date + time row */
const INPUT_ROW_HEIGHT = 40;

/** Compact chip styling (aligned with CreateNewTask status/priority pills) */
const scheduleChipSx = (extra = {}) => ({
  textTransform: 'none',
  borderRadius: '999px',
  fontSize: 11,
  px: 1.25,
  py: '3px',
  minHeight: 24,
  lineHeight: 1.2,
  fontWeight: 500,
  ...extra,
});

const DateTimeRangePicker = ({
  initialStartDateTime = null,
  initialEndDateTime = null,
  onChange,
  disabled = false,
}) => {
  const [startDate, setStartDate] = useState(initialStartDateTime ? dayjs(initialStartDateTime) : dayjs());
  const [endDate, setEndDate] = useState(initialEndDateTime ? dayjs(initialEndDateTime) : dayjs().add(1, 'hour'));

  const { startTime: defaultStart, endTime: defaultEnd } = getNextAvailableSlot();
  
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const [errors, setErrors] = useState({});
  const [timeAnchor, setTimeAnchor] = useState(null);
  const [activeTimeField, setActiveTimeField] = useState(null);
  const timeListRef = useRef(null);
  const selectedTimeRef = useRef(null);

  useEffect(() => {
    if (initialStartDateTime) {
      setStartDate(dayjs(initialStartDateTime));
      // Only set start time if it's a real initial value (not just current time)
      const initialTime = dayjs(initialStartDateTime).format('h:mm A');
      if (initialTime !== defaultStart) {
        setStartTime(initialTime);
      }
    }
    if (initialEndDateTime) {
      setEndDate(dayjs(initialEndDateTime));
      // Only set end time if it's a real initial value (not just current time)
      const initialTime = dayjs(initialEndDateTime).format('h:mm A');
      if (initialTime !== defaultEnd) {
        setEndTime(initialTime);
      }
    }
  }, [initialStartDateTime, initialEndDateTime, defaultStart, defaultEnd]);

  // Always set end date to same as start date
  useEffect(() => {
    setEndDate(startDate);
  }, [startDate]);

  useEffect(() => {
    const start = combineDateAndTime(startDate, startTime);
    const end = combineDateAndTime(endDate, endTime);

    if (start.isAfter(end)) {
      setErrors({ endTime: 'End time must be after start time' });
    } else {
      setErrors({});
      // Send dates in IST timezone
      onChange({ 
        startDateTime: start.tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'), 
        endDateTime: end.tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss') 
      });
    }
  }, [startDate, endDate, startTime, endTime]);

  // Update end time when start time changes (adding 30 minutes)
  useEffect(() => {
    if (activeTimeField === 'start' && timeAnchor === null) {
      const startDateTime = combineDateAndTime(startDate, startTime);
      const newEndDateTime = startDateTime.add(30, 'minute');
      setEndTime(newEndDateTime.format('h:mm A'));
    }
  }, [startTime, activeTimeField, timeAnchor]);

  // Scroll selected time into view when popover opens
  useEffect(() => {
    if (timeAnchor && timeListRef.current) {
      setTimeout(() => {
        let targetTime;
        
        // Determine which time to scroll to
        if (activeTimeField === 'start') {
          if (initialStartDateTime) {
            // If initialStartDateTime exists, scroll to that time
            targetTime = dayjs(initialStartDateTime).format('h:mm A');
          } else {
            // Otherwise scroll to current time
            targetTime = startTime;
          }
        } else {
          if (initialEndDateTime) {
            // If initialEndDateTime exists, scroll to that time
            targetTime = dayjs(initialEndDateTime).format('h:mm A');
          } else {
            // Otherwise scroll to current end time
            targetTime = endTime;
          }
        }
        
        // Find the time element by text content
        const allTimeElements = timeListRef.current.querySelectorAll('[data-time]');
        let targetElement = null;
        
        for (const element of allTimeElements) {
          if (element.textContent.trim() === targetTime) {
            targetElement = element;
            break;
          }
        }
        
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
        } else {
          timeListRef.current.scrollTop = 0;
        }
      }, 300);
    }
  }, [timeAnchor, activeTimeField, startTime, endTime, initialStartDateTime, initialEndDateTime]);

  const combineDateAndTime = (date, timeStr) => {
    // Parse the time string (e.g., "2:00 PM")
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 24-hour format
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    // Create a new date with the correct time in IST
    return date.hour(hours).minute(minutes).second(0).tz('Asia/Kolkata', true);
  };



  const handleTimeClick = (field, event) => {
    if (disabled) return; // Don't open time picker if disabled
    
    setActiveTimeField(field);
    setTimeAnchor(event.currentTarget);
    
    // Always set to next 15-minute slot for start time
    if (field === 'start') {
      const { startTime: nextSlot } = getNextAvailableSlot();
      setStartTime(nextSlot);
    } else if (field === 'end') {
      // For end time, we default to start time + 30 min
      const startDateTime = combineDateAndTime(startDate, startTime);
      const newEndDateTime = startDateTime.add(30, 'minute');
      setEndTime(newEndDateTime.format('h:mm A'));
    }
  };

  const handleTimeSelect = (time) => {
    if (activeTimeField === 'start') {
      // Use the new selected time for start
      const startDateTime = combineDateAndTime(startDate, time);
      const newEndDateTime = startDateTime.add(30, 'minute');
      setStartTime(time);
      setEndTime(newEndDateTime.format('h:mm A'));
    }
    if (activeTimeField === 'end') {
      // Use the new selected time for end, and the current start time for start
      setEndTime(time);
    }
    setTimeAnchor(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Quick Date Selection */}
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: 'Today', date: dayjs() },
            { label: 'Tomorrow', date: dayjs().add(1, 'day') },
            { label: 'Next Week', date: dayjs().add(7, 'day') }
          ].map((option) => (
            <Button
              key={option.label}
              variant={startDate.isSame(option.date, 'day') ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setStartDate(option.date);
                setEndDate(option.date);
              }}
              disabled={disabled}
              sx={scheduleChipSx({
                fontWeight: startDate.isSame(option.date, 'day') ? 600 : 500,
              })}
            >
              {option.label}
            </Button>
          ))}
        </Box>

        {/* Date + time: equal width, equal height (no floating label notch — label is above) */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Start date & time
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 1.5,
              width: '100%',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
              <DatePicker
                value={startDate}
                onChange={(newVal) => setStartDate(newVal)}
                disabled={disabled}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    disabled,
                    placeholder: 'DD/MM/YYYY',
                    hiddenLabel: true,
                    slotProps: {
                      input: { notched: false },
                    },
                    sx: {
                      m: 0,
                      '& .MuiOutlinedInput-notchedOutline legend': {
                        maxWidth: '0 !important',
                        width: '0 !important',
                        fontSize: 0,
                        lineHeight: 0,
                        padding: 0,
                      },
                      '& .MuiOutlinedInput-notchedOutline legend span': {
                        padding: '0 !important',
                        width: '0 !important',
                        minWidth: '0 !important',
                        display: 'block',
                      },
                      '& .MuiOutlinedInput-root': {
                        height: INPUT_ROW_HEIGHT,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        bgcolor: 'background.paper',
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 0,
                        height: INPUT_ROW_HEIGHT,
                        boxSizing: 'border-box',
                      },
                    },
                  },
                }}
              />
            </Box>
            <Button
              variant="outlined"
              onClick={(e) => handleTimeClick('start', e)}
              disabled={disabled}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 120 },
                minHeight: INPUT_ROW_HEIGHT,
                maxHeight: INPUT_ROW_HEIGHT,
                px: 1.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                bgcolor: 'background.paper',
                borderWidth: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              {startTime}
            </Button>
          </Box>
        </Box>

        {/* Quick Duration Buttons */}
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: '15 min', duration: 15 },
            { label: '30 min', duration: 30 },
            { label: '1 hour', duration: 60 },
            { label: '2 hours', duration: 120 },
            { label: '4 hours', duration: 240 },
          ].map((option) => {
            // Calculate if this duration is currently selected
            const startDateTime = combineDateAndTime(startDate, startTime);
            const currentEndDateTime = combineDateAndTime(endDate, endTime);
            const durationDiff = currentEndDateTime.diff(startDateTime, 'minute');
            
            let isSelected = false;
            isSelected = Math.abs(durationDiff - option.duration) < 5; // 5 minute tolerance
            return (
              <Button
                key={option.label}
                variant={isSelected ? "contained" : "outlined"}
                size="small"
                onClick={() => {
                  const startDateTime = combineDateAndTime(startDate, startTime);
                  const newEndDateTime = startDateTime.add(option.duration, 'minute');
                  setEndTime(newEndDateTime.format('h:mm A'));
                }}
                disabled={disabled}
                sx={{
                  ...scheduleChipSx({
                    fontWeight: isSelected ? 600 : 500,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    color: isSelected ? 'white' : 'text.secondary',
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                    },
                  }),
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </Box>
      </Box>

      {errors.endTime && (
        <Typography color="error" fontSize="0.8rem" mt={1}>
          {errors.endTime}
        </Typography>
      )}

      <Popover
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 160,
            maxHeight: 300,
            overflowY: 'auto',
            mt: 0.5,
            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px'
          }
        }}
      >
        <Box ref={timeListRef}>
          {timeOptions.map((time) => (
            <Box
              key={time}
              data-time={time}
              onClick={() => handleTimeSelect(time)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                fontSize: '0.875rem',
                backgroundColor: 
                  (activeTimeField === 'start' && time === startTime) || 
                  (activeTimeField === 'end' && time === endTime) 
                    ? '#f0f7ff' 
                    : 'transparent',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              {time}
            </Box>
          ))}
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateTimeRangePicker;
